import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import ClientOnboardingWizard from "@/components/ClientOnboardingWizard";
import EmployeeOnboardingWizard from "@/components/EmployeeOnboardingWizard";
import VendorInviteWizard from "@/components/VendorInviteWizard";
import AddExpenseDialog from "@/components/AddExpenseDialog";
import GlobalInvoiceWizard from "@/components/GlobalInvoiceWizard";
import NewPurchaseRequestDialog from "@/components/NewPurchaseRequestDialog";
import ApplyLeaveDialog from "@/components/ApplyLeaveDialog";
import AddQuoteDialog from "@/components/AddQuoteDialog";
import GrievanceSubmitDialog from "@/components/GrievanceSubmitDialog";
import { Plus, FolderPlus, UserPlus, Briefcase, Factory, Receipt, FileText, ShoppingCart, Wallet, ChevronDown, ShieldCheck, Banknote, CalendarDays, FileSignature } from "lucide-react";

// Feb 2026 — Permission Unification: every item now gates on the
// Access Management feature matrix (`permission`) instead of a hardcoded
// role list. "Raise purchase request" has no `permission` key on purpose —
// backend (`require_internal`) already lets every non-client/vendor staff
// member file one, so it stays open to all logged-in internal users.
const ITEMS = [
  { id: "new-project",      label: "New project",       icon: FolderPlus,   href: "/my-work?tab=projects&new=1",     permission: "projects_create" },
  { id: "onboard-client",   label: "Onboard client",    icon: Briefcase,    action: "wizard:client",                 permission: "clients_create" },
  { id: "onboard-employee", label: "Onboard employee",  icon: UserPlus,     action: "wizard:employee",               permission: "team_create" },
  { id: "onboard-vendor",   label: "Onboard vendor",    icon: Factory,      action: "wizard:vendor",                 permission: "vendors_onboard" },
  { id: "new-invoice",      label: "New invoice",       icon: Receipt,      action: "dialog:invoice",                permission: "finance" },
  { id: "new-contract",     label: "New contract",      icon: FileText,     href: "/crm?tab=contracts&new=1",        permission: "contracts_create", stub: true },
  { id: "new-pr",           label: "Raise purchase request", icon: ShoppingCart, href: "/studio?tab=finance&sub=purchases&new=1" },
  { id: "new-expense",      label: "New expense",       icon: Wallet,       action: "dialog:expense",                permission: "finance" },
];

// Feb 2026 — Home Dashboard "Command Center" bar: a deliberately DIFFERENT,
// narrower curated set from the topbar "Create" dropdown above — every
// item without a `permission` key opens its dialog DIRECTLY on the
// Dashboard (never an `href` navigation into a FeatureGate-protected page
// like /studio), so these 5 self-service actions truly need no Finance/HR/
// Leaves page-level access. Only "Onboard vendor" and "New invoice" keep
// their existing feature gates — everyone else already has them.
const BAR_ITEMS = [
  { id: "onboard-vendor",   label: "Onboard vendor",          icon: Factory,      action: "wizard:vendor",         permission: "vendors_onboard" },
  { id: "new-invoice",      label: "New invoice",             icon: Receipt,      action: "dialog:invoice",        permission: "finance" },
  { id: "new-pr",           label: "Raise purchase request",  icon: ShoppingCart, action: "dialog:pr" },
  { id: "submit-grievance", label: "Submit grievance",        icon: ShieldCheck,  action: "dialog:grievance" },
  { id: "new-reimbursement",label: "New reimbursement",       icon: Banknote,     action: "dialog:reimbursement" },
  { id: "new-leave",        label: "New leave",               icon: CalendarDays, action: "dialog:leave" },
  { id: "add-quote",        label: "Add a quote",              icon: FileSignature, action: "dialog:quote" },
];

export default function CreateMenu({ variant = "dropdown" }) {
  const { user } = useAuth();
  const can = usePermissions();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [clientWiz, setClientWiz] = useState(false);
  const [empWiz, setEmpWiz] = useState(false);
  const [vendorWiz, setVendorWiz] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  // Bar-only dialogs (the 5 Global-Access self-service submissions).
  const [prOpen, setPrOpen] = useState(false);
  const [reimbOpen, setReimbOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [grievanceOpen, setGrievanceOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user || user.role === "client") return null;
  const filterItems = (list) => list.filter((i) => !i.permission || can(i.permission));
  const items = filterItems(ITEMS);
  const barItems = filterItems(BAR_ITEMS);

  const handle = (it) => {
    setOpen(false);
    if (it.action === "wizard:client") setClientWiz(true);
    else if (it.action === "wizard:employee") setEmpWiz(true);
    else if (it.action === "wizard:vendor") setVendorWiz(true);
    else if (it.action === "dialog:expense") setExpenseOpen(true);
    else if (it.action === "dialog:invoice") setInvoiceOpen(true);
    else if (it.action === "dialog:pr") setPrOpen(true);
    else if (it.action === "dialog:grievance") setGrievanceOpen(true);
    else if (it.action === "dialog:reimbursement") setReimbOpen(true);
    else if (it.action === "dialog:leave") setLeaveOpen(true);
    else if (it.action === "dialog:quote") setQuoteOpen(true);
    else if (it.href) nav(it.href);
  };

  // Phase 105 — Home dashboard "Command Center", now with its own curated
  // BAR_ITEMS (see above) instead of mirroring the topbar dropdown.
  if (variant === "bar") {
    if (barItems.length === 0) return null;
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="home-command-center">
        {barItems.map((it) => {
          const Icon = it.icon;
          return (
            <button
              key={it.id}
              onClick={() => handle(it)}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-[#F54800] hover:text-[#F54800] text-slate-700 text-xs font-medium rounded-sm px-3 py-2 transition-colors"
              data-testid={`home-create-${it.id}`}
            >
              <Icon size={13} />
              {it.label}
            </button>
          );
        })}
        <VendorInviteWizard          open={vendorWiz}      onOpenChange={setVendorWiz}      onCreated={() => {}} />
        <GlobalInvoiceWizard         open={invoiceOpen}    onOpenChange={setInvoiceOpen}    onSaved={() => {}} />
        <NewPurchaseRequestDialog    open={prOpen}         onOpenChange={setPrOpen}         defaultType="purchase"      onSaved={() => {}} />
        <NewPurchaseRequestDialog    open={reimbOpen}      onOpenChange={setReimbOpen}      defaultType="reimbursement" onSaved={() => {}} />
        <ApplyLeaveDialog            open={leaveOpen}      onOpenChange={setLeaveOpen}      onSaved={() => {}} />
        <AddQuoteDialog              open={quoteOpen}      onOpenChange={setQuoteOpen}      onSaved={() => {}} />
        <GrievanceSubmitDialog       open={grievanceOpen}  onOpenChange={setGrievanceOpen}  onSubmitted={() => {}} />
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 bg-[#F54800] hover:bg-[#D63E00] text-white text-xs font-semibold rounded-sm px-3 py-2 transition-colors"
        data-testid="topbar-create"
      >
        <Plus size={14} /> Create
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-sm shadow-lg z-40 py-1"
          data-testid="topbar-create-menu"
        >
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                onClick={() => handle(it)}
                className="w-full text-left px-3 py-2 flex items-center gap-2.5 text-xs text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                data-testid={`create-${it.id}`}
              >
                <Icon size={13} className="text-slate-500" />
                <span className="flex-1">{it.label}</span>
                {it.stub && <span className="text-[9px] text-slate-400 uppercase tracking-wider">soon</span>}
              </button>
            );
          })}
        </div>
      )}
      <ClientOnboardingWizard   open={clientWiz} onOpenChange={setClientWiz} onCreated={() => {}} />
      <EmployeeOnboardingWizard open={empWiz}    onOpenChange={setEmpWiz}    onCreated={() => {}} />
      <VendorInviteWizard       open={vendorWiz} onOpenChange={setVendorWiz} onCreated={() => {}} />
      <AddExpenseDialog         open={expenseOpen} onOpenChange={setExpenseOpen} onSaved={() => {}} />
      <GlobalInvoiceWizard      open={invoiceOpen} onOpenChange={setInvoiceOpen} onSaved={() => {}} />
    </div>
  );
}
